import { z } from "zod/v4"
import * as real from "src/services/auth.service"
import * as mock from "src/services/auth.mock"
import { getDeviceDate } from "@myvp/shared/src/clock/get-device-date"
import {
  startTimer,
  logger,
  endTimer,
} from "@myvp/shared/src/beacons/beacons.model"
import { getLocale } from "src/i18n/get-locale"
import { MfaType, SupportedLocale } from "src/types"
import { GetUserMetadataQuery, Role, ValidateDobInput } from "src/gql/graphql"
import { parseJwt } from "@myvp/shared/src/functions/parse-jwt"
import { refreshOpenTabs } from "src/functions/refresh-open-tabs"
import { isSessionValid } from "@myvp/shared/src/functions/is-session-valid"
import { selectService } from "@myvp/shared/src/functions/mock"
import { envs } from "src/functions/env-utils"

const service =
  process.env.REACT_APP_release === "true"
    ? real
    : selectService({ mock, real })

export interface UnregisteredAuthResponse {
  acctVerified: boolean
  firstName?: string
  language: string
  lastName?: string
  locale?: SupportedLocale
  mfaSent: boolean
  phoneNumber?: string
}

export const resendMfaCode = async (
  args: { username?: string; email?: string | null; mfaType?: MfaType } = {}
) => {
  const locale = getLocale()
  return service.resendMfaCode({ ...args, locale })
}

export const enterMfaCode = async ({
  code,
  mfaType,
}: {
  code: string
  mfaType: MfaType
}) => {
  // Trigger event listener to remove user's metadata stored in sessionStorage
  localStorage.setItem("myvp-login", getDeviceDate().getTime().toString())
  startTimer("ACTIVITY_PATIENT_LOGIN_MFA_SERVER")
  const result = await service.enterMfaCode({
    code,
    mfaType,
  })
  endTimer("ACTIVITY_PATIENT_LOGIN_MFA_SERVER")
  return result
}

export const removeUserMetadata = () => {
  return service.removeUserMetadata()
}

export const login = async (input: {
  username: string
  password: string
  notificationRequestId: string
  ref: string
  baseUrl: string
}) => {
  if (isSessionValid()) {
    await logout()
  } else {
    resetAccountStorage()
  }
  refreshOpenTabs()
  let result = await service.login(input)
  result.bearerToken = result._httpHeaders?.get("x-atkn")
  result.frsc_t = result._httpHeaders?.get("frsc_t")
  result.frsc_k = result._httpHeaders?.get("frsc_k")
  return result
}

export const inPersonLogin = async (args: {
  authId: string
  authToken: string
}) => {
  return service.inPersonLogin(args)
}

export const reauth = async (input: { password: string }) =>
  service.reauth(input)

export const persistUserMetadata = (metadata: GetUserMetadataQuery): void => {
  const result = userMetadataSchema.safeEncode(metadata)
  if (!result.success) {
    // oxlint-disable-next-line no-console
    console.warn(
      "Tried to persist invalid user metadata in sessionStorage",
      result.error
    )
  } else {
    service.persistUserMetadata(result.data)
  }
}

const userMetadataSchema = z.strictObject({
  userInfo: z.strictObject({
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    formattedPhoneNumber: z.string().nullable().optional(),
    phoneNumber: z.string().nullable().optional(),
    pendingEmail: z.string().nullable().optional(),
    timeZoneId: z.string().nullable().optional(),
    username: z.string().nullable().optional(),
    newTermsVersion: z.string().nullable().optional(),
    authId: z.string(),
  }),
  userStudies: z.strictObject({
    userStudyRoles: z.array(
      z.strictObject({
        role: z.enum(Role),
        toDoCount: z.number(),
        isDefaultStudy: z.boolean(),
        languageTag: z.string(),
        timeZoneLabel: z.string(),
        relatedUserRoleId: z.string(),
        studyDetails: z.strictObject({
          studyName: z.string().nullable().optional(),
          studyId: z.string().nullable().optional(),
        }),
        participantDetails: z.strictObject({
          firstName: z.string(),
          lastName: z.string(),
          relatedUserId: z.string(),
          siteVaultParticipantId: z.string(),
        }),
      })
    ),
  }),
  getDoesAccountUseVirtualTime: z.strictObject({
    virtualTimeAllowed: z.boolean(),
  }),
  // NOTE: this satisfies check doesn't work for optional properties.
}) satisfies z.ZodType<GetUserMetadataQuery>

export const getUserMetadata = async (): Promise<
  z.output<typeof userMetadataSchema>
> => {
  const cachedMetadata: unknown = await service.getUserMetadata()
  if (cachedMetadata) {
    // In the event we deploy an app update and the user is on the tab and refreshes, we want to make
    // sure that we refetch in the event that the data in localStorage no longer matches the schema.
    const result = userMetadataSchema.safeParse(cachedMetadata)
    if (result.success) {
      return result.data
    } else {
      // oxlint-disable-next-line no-console
      console.warn(
        "Found invalid user metadata in sessionStorage",
        result.error
      )
    }
  }
  // NOTE: call `.parse` to make sure the graphql query + zod schema match
  const metadata = userMetadataSchema.parse(await service.getUserInfo())
  persistUserMetadata(metadata)
  return metadata
}
export const logout = async () => {
  try {
    // MYVC-1131: If the logout fails then let the user proceed
    await service.logout()
  } catch {}
  resetAccountStorage()
}

const resetAccountStorage = () => {
  const preferredMfaType = localStorage.getItem("mfa-type")
  localStorage.clear()
  if (preferredMfaType) {
    localStorage.setItem("mfa-type", preferredMfaType)
  }

  sessionStorage.clear()
}

export const validateEmailCode = (input: {
  code: string
  username?: string
  newEmail?: string
  mfaCode?: string
}) => service.validateEmailCode(input)

export const validateNewEmailCode = async (input: {
  code?: string
  newEmail?: string
  mfaCode?: string
  mfaType?: MfaType
}) => {
  const response = await service.validateNewEmailCode(input)
  logger("ACTIVITY_EMAIL_UPDATE_COMPLETED")
  return response
}

export const requestForgotPasswordCode = (args: {
  username: string
  baseUrl?: string
}) => service.requestForgotPasswordCode(args)

export const verifyMfaCode = (input: { code: string; mfaType: MfaType }) =>
  service.verifyMfaCode(input)

export const userDiscovery = async (args: { username: string }) => {
  const currentHostname = window.location.hostname
  const siblings =
    envs
      .find((env) =>
        env.urls.some((url) => new URL(url).hostname === currentHostname)
      )
      ?.urls?.filter((url) => new URL(url).hostname !== currentHostname) ?? []

  const targets = [undefined, ...siblings]

  // specifically try sequentially instead of a Promise.any or Promise.race
  for (const url of targets) {
    const signal = AbortSignal.timeout(5_000)
    try {
      const result = await service.userDiscovery(
        {
          username: args.username,
          baseUrl: url,
        },
        signal
      )
      if (result) {
        return result
      }
    } catch (e) {
      // oxlint-disable-next-line no-console
      console.warn(`Discovery failed for ${url ?? "primary"}:`, e)
    }
  }

  throw new Error(
    "All endpoints failed user discovery (server error or timeout)"
  )
}

export const updatePhoneNumber = async (args: {
  shouldClearMetadata?: boolean
  updatedPhoneNumber: string
  mfaType?: MfaType
}) => {
  const locale = getLocale()
  logger("ACTIVITY_PHONE_NUMBER_UPDATE_INITIATED")
  const response = await service.updatePhoneNumber({
    ...args,
    locale,
  })
  if (args.shouldClearMetadata) {
    // There is no user provider yet - cached metadata is invalid if user went back in browser
    sessionStorage.removeItem("metadata")
  }
  return response
}

export const verifyUpdatedPhoneNumber = async (args: {
  code: string
  updatedPhoneNumber: string
  mfaType: MfaType
  locale: string
}) => {
  const response = await service.verifyUpdatedPhoneNumber(args)
  logger("ACTIVITY_PHONE_NUMBER_UPDATE_COMPLETED")
  return response
}

export const updateEmail = (input: {
  newEmail: string
  forceEmailSend?: boolean
  locale: string
}) => {
  logger("ACTIVITY_EMAIL_UPDATE_INITIATED")
  return service.updateEmail(input)
}

export const verifyEmailMfa = async (token: string) => {
  const result = await service.verifyEmailMfa(token)
  const bearerToken = result._httpHeaders?.get("x-atkn")
  const isVerified = bearerToken && !!parseJwt(bearerToken)?.ver
  return { ...result, isVerified }
}

export const verifyEmail = (token: string) => {
  return service.verifyEmail(token)
}

export const isValidVaultSession = () => service.isValidVaultSession()

export const setUsername = async (args: {
  shouldClearMetadata?: boolean
  username: string
}) => {
  const response = await service.setUsername(args.username)
  if (args.shouldClearMetadata) {
    sessionStorage.removeItem("metadata")
  }
  return response
}

export const setPassword = async (password: string) => {
  const response = await service.setPassword(password)
  let email = response.email
  if (typeof email !== "string") {
    email = ""
  }
  let phoneNumber = ""
  if (
    typeof response.phoneCountryCode === "string" &&
    typeof response.validatedPhoneNum === "string"
  ) {
    phoneNumber = "+" + response.phoneCountryCode + response.validatedPhoneNum
  }
  return {
    phoneNumber,
    email,
  }
}

export const setEmail = async (email: string) => {
  const response = await service.setEmail(email)
  sessionStorage.removeItem("metadata")
  return response
}

export const validateYob = (input: ValidateDobInput) =>
  service.validateYob(input)

export const authorizeMfaCodes = (args: {
  username: string
  emailCode: string
  mfaCode: string
  mfaType?: string
}) => service.authorizeMfaCodes(args)

export const validateNewActivationCode = (activationCode: string) =>
  service.validateNewActivationCode(activationCode.toUpperCase())

export const confirmTerms = (versionNumber: string) => {
  return service.confirmTerms(versionNumber)
}
